import { TAffiliateFirm } from '@commenda/types';
import {
  UserEntityAccessRelationshipEntityType,
  UserEntityLinkRole,
} from '@prisma/client';

/** One row of the viewer's organizations list, as the enterprise config context shapes it. */
export type TViewerOrganization = {
  id: string;
  type: UserEntityAccessRelationshipEntityType;
  role: UserEntityLinkRole;
};

/** The viewer's affiliate firm and their role in it; null for company-only users. */
export type TAffiliateViewerMembership = {
  affiliateFirmId: string;
  role: UserEntityLinkRole;
};

export class AffiliateFirmHelper {
  static getRole(role: UserEntityLinkRole) {
    return role === UserEntityLinkRole.AFFILIATE_ADMIN ? 'Admin' : 'User';
  }

  /** The AFFILIATE_FIRM entry stays in the organizations list after the viewer switches into a
   * client workspace, so this holds on every page of the enterprise app. */
  static getViewerMembership(
    organizations: readonly TViewerOrganization[]
  ): TAffiliateViewerMembership | null {
    const firm = organizations.find(
      (organization) =>
        organization.type ===
        UserEntityAccessRelationshipEntityType.AFFILIATE_FIRM
    );

    return firm ? { affiliateFirmId: firm.id, role: firm.role } : null;
  }

  /** Whether the viewer may run operator actions on a matter: an affiliate admin whose firm
   * reaches the matter's client. The backend is the real gate; this only decides what renders. */
  static canOperateServiceRequest(
    membership: TAffiliateViewerMembership | null,
    firmClients: readonly { id: number }[],
    serviceRequest: { companyId: number | null }
  ): boolean {
    if (membership?.role !== UserEntityLinkRole.AFFILIATE_ADMIN) return false;
    if (serviceRequest.companyId == null) return false;

    return firmClients.some((client) => client.id === serviceRequest.companyId);
  }

  /** The clients a member may reach: every firm client for an admin, assigned clients only for
   * a user. */
  static getReachableClients(
    userId: string,
    role: UserEntityLinkRole,
    affiliateFirm: TAffiliateFirm
  ) {
    return role === UserEntityLinkRole.AFFILIATE_ADMIN
      ? affiliateFirm.clients
      : AffiliateFirmHelper.getAssignedClients(userId, affiliateFirm);
  }

  static getNumberOfAssignedClients(
    userId: string,
    affiliateFirm: TAffiliateFirm
  ) {
    return (
      affiliateFirm.clients.filter((client) =>
        client.assignedUserIds.includes(userId)
      ).length ?? 0
    );
  }

  static getNumberOfAssignedUsers(
    clientId: number,
    affiliateFirm: TAffiliateFirm
  ) {
    return (
      affiliateFirm.clients.find((client) => client.id === clientId)
        ?.assignedUserIds.length ?? 0
    );
  }

  static getAssignedClients(userId: string, affiliateFirm: TAffiliateFirm) {
    return affiliateFirm.clients.filter((client) =>
      client.assignedUserIds.includes(userId)
    );
  }

  static getAssignedUsers(clientId: number, affiliateFirm: TAffiliateFirm) {
    return affiliateFirm.users.filter((user) =>
      user.assignedClientIds.includes(clientId)
    );
  }
}
